import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { resetPassword, sendCode, verifyCode } from "apiHandler/actions";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./styles.css";
import { useUserData } from "components/customHooks/userDataContext";

const ResetPasswordModal = ({ open, onClose, user = {} }) => {
  const [step, setStep] = useState(1);
  const [isEmail, setIsEmail] = useState(true);
  const [uniqueId, setUniqueId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [password, setPassword] = useState("");
  const [manualEmail, setManualEmail] = useState(user?.user_id || "");
  const [manualPhone, setManualPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(""); // Add error state
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [resendTimer, setResendTimer] = useState(0);
  const userData = useUserData();
  const token = userData?.access_token;

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    if (open && user?.user_id) {
      setManualEmail(user.user_id);
    }
  }, [open, user?.user_id]);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleSendCode = async () => {
    setLoading(true);
    setError("");

    try {
      const emailId = manualEmail.trim();
      const phoneNumber = manualPhone?.trim();

      if (isEmail) {
        if (!emailId) {
          setError("Please enter an email address");
          setSnackbarOpen(true);
          return;
        }
        if (!validateEmail(emailId)) {
          setError("Please enter a valid email address");
          setSnackbarOpen(true);
          return;
        }
      }

      if (!isEmail && !phoneNumber) {
        setError("Please enter a phone number");
        setSnackbarOpen(true);
        return;
      }

      const payload = {
        emailId: isEmail ? emailId : "",
        phoneNumber: !isEmail ? phoneNumber : "",
        sendCodeToEmail: isEmail,
        sendCodeToPhone: !isEmail,
      };

      const res = await sendCode(payload);
      if (res?.uniqueId) {
        setUniqueId(res.uniqueId);
        setStep(2);
        setResendTimer(15);
      } else {
        setError(res?.message || "Failed to send code");
        setSnackbarOpen(true);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong");
      setSnackbarOpen(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setLoading(true);
    try {
      const res = await verifyCode(uniqueId, parseInt(verificationCode), token);
      if (res?.message === "Code verification success") {
        setStep(3);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onCloseModal = () => {
    setUniqueId(null);
    setStep(1);
    setManualPhone("");
    setManualEmail(user?.user_id || "");
    setPassword("");
    setVerificationCode("");
    setIsEmail(true);
    onClose();
  };

  const handleResetPassword = async () => {
    setLoading(true);
    setError("");

    try {
      const emailId = manualEmail.trim();

      if (!emailId) {
        setError("Email is required");
        setSnackbarOpen(true);
        return;
      }
      if (!validateEmail(emailId)) {
        setError("Please enter a valid email");
        setSnackbarOpen(true);
        return;
      }

      const res = await resetPassword(emailId, password, token);
      if (res?.message === "password reset success") {
        onClose();
      } else {
        setError(res?.message || "Failed to reset password");
        setSnackbarOpen(true);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Reset failed");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Reset Password</DialogTitle>
      <DialogContent>
        {step === 1 && (
          <Box>
            <Typography variant="body2" gutterBottom>
              Send verification code to:
            </Typography>
            <Box mb={2}>
              <Button
                variant={isEmail ? "contained" : "outlined"}
                onClick={() => setIsEmail(true)}
                sx={{ mr: 1 }}
              >
                Email
              </Button>
              <Button
                variant={!isEmail ? "contained" : "outlined"}
                onClick={() => setIsEmail(false)}
              >
                Phone
              </Button>
            </Box>

            {isEmail && (
              <TextField
                fullWidth
                margin="dense"
                label="Enter Email"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
                sx={{ mb: 2 }}
              />
            )}
            {!isEmail && (
              <PhoneInput
                international
                defaultCountry="US"
                placeholder="Enter phone number"
                value={manualPhone}
                onChange={(e) => setManualPhone(e)}
                className="phone-input"
              />
            )}
            <Button
              variant="text"
              size="small"
              onClick={() => setStep(2)}
              sx={{ mt: 1, textTransform: "none" }}
            >
              Already have a verification code?
            </Button>
          </Box>
        )}
        {step === 2 && (
          <Box>
            <TextField
              fullWidth
              margin="dense"
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
            />

            <Box mt={2} display="flex" justifyContent="space-between">
              <Button
                variant="text"
                onClick={handleSendCode}
                disabled={resendTimer > 0}
              >
                {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend Code"}
              </Button>
            </Box>
          </Box>
        )}

        {step === 3 && (
          <TextField
            fullWidth
            margin="dense"
            type="password"
            label="New Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCloseModal} disabled={loading}>
          Cancel
        </Button>
        {step === 1 && (
          <Button
            onClick={handleSendCode}
            disabled={loading || resendTimer > 0}
          >
            {loading ? <CircularProgress size={20} /> : "Send Code"}
          </Button>
        )}
        {step === 2 && (
          <Button onClick={handleVerifyCode} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Verify"}
          </Button>
        )}
        {step === 3 && (
          <Button onClick={handleResetPassword} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Reset"}
          </Button>
        )}
      </DialogActions>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ResetPasswordModal;
